import { takeLatest, put, call, delay, all } from 'redux-saga/effects';
import get from 'lodash/get';
import { find } from 'lodash';
import {
  getArtistNamesAction,
  uploadSeveralMediaAction,
  uploadProductionDocumentsAction,
  updateProductionSynopsisDocumentsAction,
  createDigitalEventAction,
  updateProductionAction,
  deleteDigitalEventAction,
} from './actions';
// eslint-disable-next-line import/no-cycle
import * as API from '../../utils/API';
import endpointConstants from '../../constants/endpointConstants';
import { ARTIST_TYPE_ID, VISIBILITY } from '../../constants/consts';
import { enqueueSnackbarAction } from '../App/actions';
import { musicalWorkFields } from '../../constants/formFields';
import { formatResponseToEntity } from '../../utils/crudUtils';

export function* fetchArtistNames({ payload }) {
  try {
    yield delay(300);
    const response = yield call(
      API.searchProfiles,
      ARTIST_TYPE_ID,
      payload || '',
      {},
      { show_invalid: true, visibility: VISIBILITY.ALL },
    );
    yield put(getArtistNamesAction.success(get(response, 'data')));
  } catch (err) {
    yield put(getArtistNamesAction.failure(err.message));
  } finally {
    yield put(getArtistNamesAction.fulfill());
  }
}

export function* uploadMedia({ payload }) {
  try {
    const data = get(payload, 'data', []);
    const response = yield all(data.map(media => call(API.create, endpointConstants.MEDIA, media?.data, media?.file)));
    yield put(uploadSeveralMediaAction.success(response?.map(r => r?.data)));
    const callback = payload?.callback;
    if (callback && typeof callback === 'function') payload.callback();
    yield put(
      enqueueSnackbarAction({
        message: `Success adding images`,
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: `Error adding images: ${err.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
    yield put(uploadSeveralMediaAction.failure(err.message));
  }
}

export function* uploadDocuments({ payload }) {
  try {
    const data = get(payload, 'data', []);
    const files = get(payload, 'files', {});
    const response = yield all(
      data.map(document => {
        const file = document?.file?.data ? files[document?.file?.data] : null;
        return call(
          API.create,
          endpointConstants.PRODUCTION_DOCUMENTS,
          document,
          file && { [document?.file?.data.slice(1)]: file },
        );
      }),
    );
    yield put(uploadProductionDocumentsAction.success(response?.map(r => r?.data)));
    const callback = payload?.callback;
    if (callback && typeof callback === 'function') payload.callback();
    yield put(
      enqueueSnackbarAction({
        message: `Success adding files`,
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: `Error adding files: ${err.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
    yield put(uploadProductionDocumentsAction.failure(err.message));
  }
}

export function* updateSynopsis({ payload }) {
  try {
    const created = get(payload, 'createdDocuments', []);
    const updated = get(payload, 'updatedDocuments', []);
    const deleted = get(payload, 'deletedDocuments', []);
    yield all([
      ...created.map(file => call(API.create, endpointConstants.PRODUCTION_DOCUMENTS, file)),
      ...updated.map(file => call(API.updateOne, endpointConstants.PRODUCTION_DOCUMENTS, file?.id, file)),
      ...deleted.map(id => call(API.deleteOne, endpointConstants.PRODUCTION_DOCUMENTS, id)),
    ]);
    yield put(updateProductionSynopsisDocumentsAction.success());
    const callback = payload?.callback;
    if (callback && typeof callback === 'function') payload.callback();
    yield put(
      enqueueSnackbarAction({
        message: `Success updating synopsis files`,
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: `Error updating synopsis: ${err.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
    yield put(updateProductionSynopsisDocumentsAction.failure(err.message));
  }
}

function* updateProductionTypes({ productionId, types, message }) {
  try {
    const response = yield call(API.updateOne, endpointConstants.PRODUCTIONS, productionId, {
      [musicalWorkFields.productionTypesField.productionName]: types || [],
    });
    yield put(updateProductionAction.success(formatResponseToEntity(response)));
    yield put(
      enqueueSnackbarAction({
        message,
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: `Error updating production: ${err.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* createDigitalEvent({ payload }) {
  try {
    const { params, files, callback, listLength, production, typeId, typeName } = payload;
    const response = yield call(API.create, endpointConstants.MEDIA, params, files);
    yield put(
      enqueueSnackbarAction({
        message: `${typeName} was created`,
        options: {
          variant: 'success',
        },
      }),
    );
    if (!listLength && !find(production?.performanceTypes, { id: typeId })) {
      yield call(updateProductionTypes, {
        productionId: production?.id,
        types: [...(production?.performanceTypes || []), { id: typeId }],
        message: `Production was marked as ${typeName}`,
      });
    }
    yield put(createDigitalEventAction.success({ entity: response?.data, typeId }));
    if (callback && typeof callback === 'function') payload.callback();
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: `Error creating ${payload?.typeName}: ${err.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
    yield put(createDigitalEventAction.failure(err.message));
  }
}

export function* deleteDigitalEvent({ payload }) {
  try {
    const { id, callback, listLength, production, typeId, typeName } = payload;
    yield call(API.deleteOne, endpointConstants.MEDIA, id);
    yield put(
      enqueueSnackbarAction({
        message: `${typeName} was deleted`,
        options: {
          variant: 'success',
        },
      }),
    );
    if (listLength === 1) {
      yield call(updateProductionTypes, {
        productionId: production?.id,
        types: production?.performanceTypes?.filter(type => type?.id !== typeId),
        message: `${typeName} is unchecked`,
      });
    }
    yield put(deleteDigitalEventAction.success({ id, typeId }));
    if (callback && typeof callback === 'function') payload.callback();
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: `Error creating ${payload?.typeName}: ${err.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
    yield put(deleteDigitalEventAction.failure(err.message));
  }
}

export default function* ProductionUpdateSaga() {
  yield takeLatest(getArtistNamesAction.TRIGGER, fetchArtistNames);
  yield takeLatest(uploadSeveralMediaAction.TRIGGER, uploadMedia);
  yield takeLatest(uploadProductionDocumentsAction.TRIGGER, uploadDocuments);
  yield takeLatest(updateProductionSynopsisDocumentsAction.TRIGGER, updateSynopsis);
  yield takeLatest(createDigitalEventAction.TRIGGER, createDigitalEvent);
  yield takeLatest(deleteDigitalEventAction.TRIGGER, deleteDigitalEvent);
}
